import { DrawPlayerLink } from './DrawPlayerLink';
import { Fragment, useState } from 'react';
import { DrawRound, Player, DrawStage, StageStandings, DrawMatch, GameScore } from '@/draw/types';
import { useTranslations, useLocale } from 'next-intl';
import { useFormatName } from '@/contexts/NameFormattingContext';
import { drawPlayerLabel } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import { NameText } from '@/app/[locale]/event/[eventId]/[slug]/components/NameText';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { useHydratedTimeZone } from '@/hooks/useHydratedTimeZone';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import { useOutcomeLabel } from '@/components/match/matchOutcomeLabel';

interface RoundRobinRoundVisualizationProps {
  round: DrawRound;
  players: Player[];
  playerHref?: (playerId: string) => string | undefined;
  onMatchResult?: (matchId: string, winner: Player, loser: Player, scores?: GameScore[]) => void;
  onMatchClick?: (match: DrawMatch) => void;
  showExtraInfo?: boolean;
  /** Render within a public draw panel, without nested cards or negative edge margins. */
  embedded?: boolean;
}

export function RoundRobinRoundVisualization({
  round,
  players,
  playerHref,
  onMatchResult,
  onMatchClick,
  showExtraInfo = true,
  embedded = false,
}: RoundRobinRoundVisualizationProps) {
  const _t = useTranslations('AdminDraw.drawForm.drawPreview');
  const tMatches = useTranslations('Matches');
  const outcomeLabel = useOutcomeLabel();
  const locale = useLocale();
  const tz = useHydratedTimeZone();
  const formatName = useFormatName();
  // Handle player click to simulate match result
  const handlePlayerClick = (match: DrawMatch, clickedPlayer: Player) => {
    if (!onMatchResult || match.score || match.winner || match.isBye) {
      return; // Don't allow clicks if match is already completed, is a BYE, or no callback provided
    }

    const otherPlayer = clickedPlayer.id === match.player1?.id ? match.player2 : match.player1;
    if (!otherPlayer) {
      return; // Need both players to simulate a match
    }

    // Simulate random game result (3-0, 3-1, or 3-2)
    const winner = clickedPlayer;
    const loser = otherPlayer;

    // Generate random game scores - winner needs to win 3 games
    const gameResults = [];
    let winnerGames = 0;
    let loserGames = 0;
    let gameNumber = 1;

    // Keep playing until winner gets 3 games
    while (winnerGames < 3 && loserGames < 3) {
      // Generate realistic squash game scores (games go to 11 points)
      const winnerScore = Math.floor(Math.random() * 3) + 11; // 11-13 points
      const loserScore = Math.floor(Math.random() * 9) + 2; // 2-10 points

      // Randomly decide who wins this game (but ensure winner gets 3 games)
      const gameWinner =
        (winnerGames < 2 && Math.random() < 0.7) || winnerGames === 2 ? winner : loser;

      if (gameWinner.id === winner.id) {
        winnerGames++;
        gameResults.push({
          game: gameNumber,
          player1Score: clickedPlayer.id === match.player1?.id ? winnerScore : loserScore,
          player2Score: clickedPlayer.id === match.player1?.id ? loserScore : winnerScore,
        });
      } else {
        loserGames++;
        gameResults.push({
          game: gameNumber,
          player1Score: clickedPlayer.id === match.player1?.id ? loserScore : winnerScore,
          player2Score: clickedPlayer.id === match.player1?.id ? winnerScore : loserScore,
        });
      }
      gameNumber++;
    }

    onMatchResult(match.matchId, winner, loser, gameResults);
  };
  return (
    <div className="space-y-4">
      {showExtraInfo && (
        <div className="text-center">
          <h4 className="text-foreground text-lg font-semibold">{round.name}</h4>
          <p className="text-muted text-sm">{round.matches.length} matches</p>
        </div>
      )}

      {/* Round Robin Grid Table */}
      <div className={embedded ? 'overflow-x-auto' : '-mx-6 overflow-x-auto'}>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-border bg-foreground/[0.03] border-y">
              <th className="text-muted py-2.5 pr-3 pl-6 text-left text-xs font-semibold tracking-wide uppercase">
                Player
              </th>
              {players.map((player, idx) => (
                <th
                  key={player.id}
                  className={cn(
                    'border-border text-muted border-l py-2.5 text-center text-xs font-semibold tracking-wide uppercase',
                    idx === players.length - 1 ? 'pr-6 pl-3' : 'px-3',
                    NAME_WRAP,
                  )}
                  style={{ minWidth: '80px' }}
                >
                  <DrawPlayerLink
                    playerId={player.id}
                    playerHref={player.isBye ? undefined : playerHref}
                  >
                    <NameText name={drawPlayerLabel(player, formatName)} />
                  </DrawPlayerLink>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-border divide-y">
            {players.map((player) => (
              <tr key={player.id} className="hover:bg-foreground/[0.02] transition-colors">
                <td
                  className={cn(
                    'border-border bg-foreground/[0.02] text-foreground border-r py-2.5 pr-3 pl-6 font-medium',
                    NAME_WRAP,
                  )}
                >
                  <DrawPlayerLink
                    playerId={player.id}
                    playerHref={player.isBye ? undefined : playerHref}
                  >
                    <NameText name={drawPlayerLabel(player, formatName)} />
                  </DrawPlayerLink>
                </td>
                {players.map((opponent) => {
                  if (player.id === opponent.id) {
                    return (
                      <td
                        key={opponent.id}
                        className="border-border bg-foreground/[0.04] text-muted border-l px-3 py-2.5 text-center"
                      >
                        —
                      </td>
                    );
                  }

                  // Find the match between these two players
                  const match = round.matches.find(
                    (m) =>
                      (m.player1?.id === player.id && m.player2?.id === opponent.id) ||
                      (m.player1?.id === opponent.id && m.player2?.id === player.id),
                  );

                  if (!match) {
                    return (
                      <td
                        key={opponent.id}
                        className="border-border text-muted border-l px-3 py-2.5 text-center"
                      >
                        TBD
                      </td>
                    );
                  }

                  const isPlayer1 = match.player1?.id === player.id;
                  const winnerless = isWinnerlessRow({
                    outcome: match.outcome,
                    is_walkover: match.isWalkover,
                  });

                  const isSimulatable =
                    !match.score &&
                    !match.winner &&
                    !match.isBye &&
                    !winnerless &&
                    onMatchResult &&
                    !onMatchClick;
                  const isClickableForModal = !match.isBye && onMatchClick;
                  const isClickable = isSimulatable || isClickableForModal;

                  return (
                    <td
                      key={opponent.id}
                      className={`border-border border-l px-3 py-2.5 text-center ${
                        match.winner?.id === player.id
                          ? 'bg-green-50 font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-200'
                          : 'text-foreground'
                      } ${isClickable ? 'hover:bg-accent/10 cursor-pointer' : ''}`}
                      onClick={
                        isClickableForModal
                          ? () => onMatchClick!(match)
                          : isSimulatable
                            ? () => handlePlayerClick(match, player)
                            : undefined
                      }
                    >
                      {winnerless ? (
                        match.outcome === 'walkover' ||
                        (match.outcome == null && match.isWalkover) ? (
                          <div>
                            {match.winner?.id === player.id ? (
                              <div className="text-sm font-bold text-green-600 dark:text-green-400">
                                {tMatches('winWalkover')}
                              </div>
                            ) : (
                              <div className="text-sm font-bold text-amber-600 dark:text-amber-400">
                                {tMatches('lossWalkover')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div
                            className={`text-sm font-bold ${
                              match.outcome === 'dual_withdrawal'
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-muted'
                            }`}
                          >
                            {outcomeLabel(match.outcome, false)}
                          </div>
                        )
                      ) : match.score && match.score.length > 0 ? (
                        <div>
                          <div className="text-sm">
                            {match.score.map((game, index) => {
                              const gamePlayerScore = isPlayer1
                                ? game.player1Score
                                : game.player2Score;
                              const gameOpponentScore = isPlayer1
                                ? game.player2Score
                                : game.player1Score;
                              return (
                                <span key={index}>
                                  {gamePlayerScore}-{gameOpponentScore}
                                  {index < match.score!.length - 1 ? ' ' : ''}
                                </span>
                              );
                            })}
                            {match.outcome === 'retired' && (
                              <span className="text-muted ml-1 text-xs">
                                {tMatches('retiredShort')}
                              </span>
                            )}
                          </div>
                          {match.status && <div className="text-muted text-xs">{match.status}</div>}
                        </div>
                      ) : isClickable ? (
                        <span className="text-accent font-medium">{tMatches('clickToPlay')}</span>
                      ) : match.eventMatchSchedule?.scheduled_datetime ? (
                        <div className="text-muted text-xs">
                          <span className="font-mono">{match.matchId}</span> -{' '}
                          {new Date(match.eventMatchSchedule.scheduled_datetime).toLocaleDateString(
                            locale,
                            { month: 'short', day: 'numeric', timeZone: tz },
                          )}{' '}
                          {new Date(match.eventMatchSchedule.scheduled_datetime).toLocaleTimeString(
                            locale,
                            { hour: '2-digit', minute: '2-digit', timeZone: tz },
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">vs</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface RoundRobinStageVisualizationProps {
  stage: DrawStage;
  players: Player[];
  playerHref?: (playerId: string) => string | undefined;
  standings?: StageStandings;
  onMatchResult?: (matchId: string, winner: Player, loser: Player, scores?: GameScore[]) => void;
  onMatchClick?: (match: DrawMatch) => void;
  showExtraInfo?: boolean;
  /** Render within a public draw panel, without nested cards or negative edge margins. */
  embedded?: boolean;
}

export function RoundRobinStageVisualization({
  stage,
  players,
  playerHref,
  standings,
  onMatchResult,
  onMatchClick,
  showExtraInfo = true,
  embedded = false,
}: RoundRobinStageVisualizationProps) {
  const _t = useTranslations('AdminDraw.drawForm.drawPreview');
  const tFutsal = useTranslations('FutsalScorer');
  const Panel = embedded ? 'div' : Card;
  const PanelContent = embedded ? 'div' : CardContent;
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);

  const isFutsalStandings =
    standings && standings.standings.length > 0 && standings.standings[0]?.goalsFor !== undefined;

  return (
    <div className="space-y-10">
      {showExtraInfo && (
        <>
          <h3 className="text-foreground mt-4 text-lg font-semibold">{stage.name}</h3>
          <p className="text-muted text-sm">{stage.rounds.length} rounds</p>
        </>
      )}

      {/* Standings Card */}
      {standings && standings.standings.length > 0 && (
        <Panel>
          <PanelContent>
            <h3 className="text-foreground mt-2 mb-3 text-lg font-semibold">Standings</h3>
            <div className={embedded ? 'overflow-x-auto' : '-mx-6 overflow-x-auto'}>
              {isFutsalStandings ? (
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-border bg-foreground/[0.03] border-y">
                      <th className="text-muted w-12 py-2.5 pr-3 pl-6 text-left text-xs font-semibold tracking-wide uppercase">
                        #
                      </th>
                      <th className="text-muted px-3 py-2.5 text-left text-xs font-semibold tracking-wide uppercase">
                        Team
                      </th>
                      <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                        Pts
                      </th>
                      <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                        W
                      </th>
                      <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                        D
                      </th>
                      <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                        L
                      </th>
                      <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                        GF
                      </th>
                      <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                        GA
                      </th>
                      <th className="text-muted py-2.5 pr-6 pl-3 text-right text-xs font-semibold tracking-wide uppercase">
                        GD
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-border divide-y">
                    {standings.standings.map((standing, index) => (
                      <Fragment key={standing.playerId}>
                        <tr
                          className={`${index === 0 ? 'bg-accent/5' : 'hover:bg-foreground/[0.02] transition-colors'} cursor-pointer`}
                          onClick={() =>
                            setExpandedTeam(
                              expandedTeam === standing.playerId ? null : standing.playerId,
                            )
                          }
                        >
                          <td className="text-foreground py-2.5 pr-3 pl-6 font-semibold tabular-nums">
                            {standing.position}
                          </td>
                          <td className={cn('text-foreground px-3 py-2.5 font-medium', NAME_WRAP)}>
                            <span className="text-muted mr-1.5 text-xs">
                              {expandedTeam === standing.playerId ? '\u25BC' : '\u25B6'}
                            </span>
                            <DrawPlayerLink playerId={standing.playerId} playerHref={playerHref}>
                              <NameText name={standing.playerName} />
                            </DrawPlayerLink>
                          </td>
                          <td className="text-foreground px-3 py-2.5 text-right font-semibold tabular-nums">
                            {standing.points}
                          </td>
                          <td className="text-foreground px-3 py-2.5 text-right tabular-nums">
                            {standing.wins}
                          </td>
                          <td className="text-foreground px-3 py-2.5 text-right tabular-nums">
                            {standing.draws ?? 0}
                          </td>
                          <td className="text-foreground px-3 py-2.5 text-right tabular-nums">
                            {standing.losses}
                          </td>
                          <td className="text-foreground px-3 py-2.5 text-right tabular-nums">
                            {standing.goalsFor ?? 0}
                          </td>
                          <td className="text-foreground px-3 py-2.5 text-right tabular-nums">
                            {standing.goalsAgainst ?? 0}
                          </td>
                          <td
                            className={`py-2.5 pr-6 pl-3 text-right font-semibold tabular-nums ${
                              (standing.goalDifference ?? 0) > 0
                                ? 'text-green-600 dark:text-green-400'
                                : (standing.goalDifference ?? 0) < 0
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-foreground'
                            }`}
                          >
                            {(standing.goalDifference ?? 0) > 0 ? '+' : ''}
                            {standing.goalDifference ?? 0}
                          </td>
                        </tr>
                        {expandedTeam === standing.playerId &&
                          standing.matchBreakdowns &&
                          standing.matchBreakdowns.length > 0 && (
                            <tr>
                              <td colSpan={9} className="bg-foreground/[0.02] px-6 py-3">
                                <div className="space-y-2">
                                  {standing.matchBreakdowns.map((breakdown) => (
                                    <div
                                      key={breakdown.matchLabel}
                                      className="flex items-baseline gap-3 text-sm"
                                    >
                                      <span className="text-muted w-8 shrink-0 font-mono text-xs">
                                        {breakdown.matchLabel}
                                      </span>
                                      <span
                                        className={cn('text-foreground w-32 shrink-0', NAME_WRAP)}
                                      >
                                        vs <NameText name={breakdown.opponentName} />
                                      </span>
                                      <span className="text-foreground w-12 shrink-0 text-center font-mono">
                                        {breakdown.score}
                                      </span>
                                      <span className="text-foreground w-8 shrink-0 text-right font-semibold tabular-nums">
                                        {breakdown.points}
                                      </span>
                                      <span className="text-muted text-xs">
                                        {breakdown.items.map((item, i) => (
                                          <span key={i}>
                                            {i > 0 && ', '}
                                            {tFutsal(item.ruleKey)} {item.points > 0 ? '+' : ''}
                                            {item.points}
                                          </span>
                                        ))}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-border bg-foreground/[0.03] border-y">
                      <th className="text-muted w-12 py-2.5 pr-3 pl-6 text-left text-xs font-semibold tracking-wide uppercase">
                        #
                      </th>
                      <th className="text-muted px-3 py-2.5 text-left text-xs font-semibold tracking-wide uppercase">
                        Player
                      </th>
                      <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                        Pts
                      </th>
                      <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                        W
                      </th>
                      <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                        L
                      </th>
                      <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                        Games
                      </th>
                      <th className="text-muted py-2.5 pr-6 pl-3 text-right text-xs font-semibold tracking-wide uppercase">
                        Game Pts
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-border divide-y">
                    {standings.standings.map((standing, index) => (
                      <tr
                        key={standing.playerId}
                        className={
                          index === 0
                            ? 'bg-accent/5'
                            : 'hover:bg-foreground/[0.02] transition-colors'
                        }
                      >
                        <td className="text-foreground py-2.5 pr-3 pl-6 font-semibold tabular-nums">
                          {standing.position}
                        </td>
                        <td className={cn('text-foreground px-3 py-2.5 font-medium', NAME_WRAP)}>
                          <DrawPlayerLink playerId={standing.playerId} playerHref={playerHref}>
                            <NameText name={standing.playerName} />
                          </DrawPlayerLink>
                        </td>
                        <td className="text-foreground px-3 py-2.5 text-right font-semibold tabular-nums">
                          {standing.points}
                        </td>
                        <td className="text-foreground px-3 py-2.5 text-right tabular-nums">
                          {standing.wins}
                        </td>
                        <td className="text-foreground px-3 py-2.5 text-right tabular-nums">
                          {standing.losses}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          <div className="text-foreground">
                            {standing.gamesWon}-{standing.gamesLost}
                          </div>
                          <div className="text-muted text-xs">
                            {standing.gamesWon + standing.gamesLost > 0
                              ? Math.round(
                                  (standing.gamesWon / (standing.gamesWon + standing.gamesLost)) *
                                    100,
                                )
                              : 0}
                            %
                          </div>
                        </td>
                        <td className="py-2.5 pr-6 pl-3 text-right tabular-nums">
                          <div className="text-foreground">
                            {standing.gamePointsFor}-{standing.gamePointsAgainst}
                          </div>
                          <div className="text-muted text-xs">
                            {standing.gamePointsPercentage.toFixed(1)}%
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </PanelContent>
        </Panel>
      )}

      {/* Results Card */}
      <Panel>
        <PanelContent>
          <h3 className="text-foreground mt-2 mb-3 text-lg font-semibold">Results</h3>
          {stage.rounds.map((round) => (
            <RoundRobinRoundVisualization
              playerHref={playerHref}
              key={round.roundId}
              round={round}
              players={players}
              onMatchResult={onMatchResult}
              onMatchClick={onMatchClick}
              showExtraInfo={showExtraInfo}
              embedded={embedded}
            />
          ))}
        </PanelContent>
      </Panel>

      {/* Match Points Matrix */}
      {isFutsalStandings && (
        <Panel>
          <PanelContent>
            <h3 className="text-foreground mt-2 mb-3 text-lg font-semibold">Match Points</h3>
            <div className={embedded ? 'overflow-x-auto' : '-mx-6 overflow-x-auto'}>
              {(() => {
                // Build match data from all standings' matchBreakdowns
                // First occurrence of a matchLabel is home, second is away
                const matchMap = new Map<
                  string,
                  {
                    matchLabel: string;
                    homeName: string;
                    awayName: string;
                    homeScore: string;
                    awayScore: string;
                    homePoints: number;
                    awayPoints: number;
                    homeItems: Array<{ ruleKey: string; points: number }>;
                    awayItems: Array<{ ruleKey: string; points: number }>;
                  }
                >();

                for (const standing of standings.standings) {
                  if (!standing.matchBreakdowns) continue;
                  for (const breakdown of standing.matchBreakdowns) {
                    const existing = matchMap.get(breakdown.matchLabel);
                    if (!existing) {
                      // First occurrence = home team
                      matchMap.set(breakdown.matchLabel, {
                        matchLabel: breakdown.matchLabel,
                        homeName: standing.playerName,
                        awayName: breakdown.opponentName,
                        homeScore: breakdown.score,
                        awayScore: '',
                        homePoints: breakdown.points,
                        awayPoints: 0,
                        homeItems: breakdown.items.map((it) => ({
                          ruleKey: it.ruleKey,
                          points: it.points,
                        })),
                        awayItems: [],
                      });
                    } else {
                      // Second occurrence = away team
                      existing.awayPoints = breakdown.points;
                      existing.awayItems = breakdown.items.map((it) => ({
                        ruleKey: it.ruleKey,
                        points: it.points,
                      }));
                      existing.awayScore = breakdown.score;
                    }
                  }
                }

                // Sort by numeric part of label (A1, A2, ...)
                const matches = Array.from(matchMap.values()).sort((a, b) => {
                  const numA = parseInt(a.matchLabel.replace(/\D/g, ''), 10) || 0;
                  const numB = parseInt(b.matchLabel.replace(/\D/g, ''), 10) || 0;
                  return numA - numB;
                });

                if (matches.length === 0) return null;

                return (
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-border bg-foreground/[0.03] border-y">
                        <th className="text-muted py-2.5 pr-3 pl-6 text-left text-xs font-semibold tracking-wide uppercase">
                          Match
                        </th>
                        <th className="text-muted px-3 py-2.5 text-left text-xs font-semibold tracking-wide uppercase">
                          Home
                        </th>
                        <th className="text-muted px-3 py-2.5 text-left text-xs font-semibold tracking-wide uppercase">
                          Away
                        </th>
                        <th className="text-muted px-3 py-2.5 text-center text-xs font-semibold tracking-wide uppercase">
                          Score
                        </th>
                        <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                          H
                        </th>
                        <th className="text-muted px-3 py-2.5 text-left text-xs font-semibold tracking-wide uppercase">
                          Breakdown
                        </th>
                        <th className="text-muted px-3 py-2.5 text-right text-xs font-semibold tracking-wide uppercase">
                          A
                        </th>
                        <th className="text-muted py-2.5 pr-6 pl-3 text-left text-xs font-semibold tracking-wide uppercase">
                          Breakdown
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-border divide-y">
                      {matches.map((m) => (
                        <tr
                          key={m.matchLabel}
                          className="hover:bg-foreground/[0.02] transition-colors"
                        >
                          <td className="text-muted py-2.5 pr-3 pl-6 font-mono text-xs">
                            {m.matchLabel}
                          </td>
                          <td
                            className={cn(
                              'text-foreground min-w-[120px] px-3 py-2.5 font-medium',
                              NAME_WRAP,
                            )}
                          >
                            <NameText name={m.homeName} />
                          </td>
                          <td
                            className={cn(
                              'text-foreground min-w-[120px] px-3 py-2.5 font-medium',
                              NAME_WRAP,
                            )}
                          >
                            <NameText name={m.awayName} />
                          </td>
                          <td className="text-foreground px-3 py-2.5 text-center font-mono tabular-nums">
                            {m.homeScore}
                          </td>
                          <td className="text-foreground px-3 py-2.5 text-right font-semibold tabular-nums">
                            {m.homePoints}
                          </td>
                          <td className="text-muted px-3 py-2.5 text-xs">
                            {m.homeItems.map((it, i) => (
                              <span key={i}>
                                {i > 0 && ', '}
                                {tFutsal(it.ruleKey)} {it.points > 0 ? '+' : ''}
                                {it.points}
                              </span>
                            ))}
                          </td>
                          <td className="text-foreground px-3 py-2.5 text-right font-semibold tabular-nums">
                            {m.awayPoints}
                          </td>
                          <td className="text-muted py-2.5 pr-6 pl-3 text-xs">
                            {m.awayItems.map((it, i) => (
                              <span key={i}>
                                {i > 0 && ', '}
                                {tFutsal(it.ruleKey)} {it.points > 0 ? '+' : ''}
                                {it.points}
                              </span>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </PanelContent>
        </Panel>
      )}
    </div>
  );
}
